/** 高於 Dialog／AlertDialog overlay／content（z-50）；且高於列表 sticky thead（z-10）。 */
const MENU_STACKING = {
  zIndex: 60,
  /** react-remove-scroll 會對 body 設 pointer-events:none；portal menu 需自行恢復 */
  pointerEvents: 'auto',
};

/**
 * react-select 預設色票（neutral0…90、primary…）改吃 CSS 變數，深色主題才會跟著換。
 * 傳給 <Select theme={selectTheme} />。
 */
export function selectTheme(theme) {
  return {
    ...theme,
    colors: {
      ...theme.colors,
      primary: 'var(--color-primary-500)',
      primary75: 'var(--color-primary-300)',
      primary50: 'var(--color-primary-200)',
      primary25: 'var(--color-primary-100)',
      neutral0: 'var(--surface-panel)',
      neutral5: 'var(--muted)',
      neutral10: 'var(--border)',
      neutral20: 'var(--color-gray-300)',
      neutral30: 'var(--color-gray-400)',
      neutral40: 'var(--muted-foreground)',
      neutral50: 'var(--muted-foreground)',
      neutral60: 'var(--color-gray-600)',
      neutral70: 'var(--color-gray-700)',
      neutral80: 'var(--foreground)',
      neutral90: 'var(--foreground)',
    },
  };
}

/**
 * 合併 base 與 override 的 react-select styles（逐 key 串接函式）。
 * menu／menuPortal 一律保留 zIndex:60 與 pointer-events:auto。
 *
 * @param {Record<string, Function>} baseStyles
 * @param {Record<string, Function>|undefined|null} overrideStyles
 */
export function mergeSelectStyles(baseStyles, overrideStyles) {
  const merged = { ...(baseStyles || {}) };

  if (overrideStyles && typeof overrideStyles === 'object') {
    Object.keys(overrideStyles).forEach((key) => {
      const baseFn = merged[key];
      const overrideFn = overrideStyles[key];
      if (typeof overrideFn !== 'function') {
        merged[key] = overrideFn;
        return;
      }
      merged[key] = (base, state) => {
        const withBase = typeof baseFn === 'function' ? baseFn(base, state) : base;
        return overrideFn(withBase, state);
      };
    });
  }

  const menuFn = merged.menu;
  merged.menu = (base, state) => ({
    ...(typeof menuFn === 'function' ? menuFn(base, state) : base),
    ...MENU_STACKING,
  });

  const menuPortalFn = merged.menuPortal;
  merged.menuPortal = (base, state) => ({
    ...(typeof menuPortalFn === 'function' ? menuPortalFn(base, state) : base),
    ...MENU_STACKING,
  });

  return merged;
}

/** @param {boolean} hasError */
export function buildSelectStyles(hasError) {
  const defaultBorder = 'var(--color-gray-300)';
  const focusBorder = 'var(--color-primary-500)';
  const errorBorder = 'var(--color-red-500)';
  const focusRing = '0 0 0 1px rgba(14, 165, 233, 0.35)';
  const errorRing = '0 0 0 1px rgba(239, 68, 68, 0.35)';

  function resolveBorderColor(isFocused) {
    if (hasError) {
      return errorBorder;
    }
    if (isFocused) {
      return focusBorder;
    }
    return defaultBorder;
  }

  function resolveBoxShadow(isFocused) {
    if (hasError && isFocused) {
      return errorRing;
    }
    if (isFocused) {
      return focusRing;
    }
    return 'none';
  }

  return {
    control: (base, state) => {
      const isFocused = state.isFocused;
      const borderColor = resolveBorderColor(isFocused);
      const hoverBorderColor = hasError ? errorBorder : focusBorder;
      const hoverRing = hasError ? errorRing : focusRing;

      if (state.isDisabled) {
        // 與唯讀 input 一致：灰底、無 hover 高亮
        return {
          ...base,
          minHeight: '3.125rem',
          backgroundColor: 'var(--color-gray-100)',
          borderColor: hasError ? errorBorder : defaultBorder,
          boxShadow: 'none',
        };
      }

      return {
        ...base,
        minHeight: '3.125rem',
        backgroundColor: 'var(--surface-field)',
        borderColor,
        boxShadow: resolveBoxShadow(isFocused),
        '&:hover': {
          borderColor: hoverBorderColor,
          boxShadow: hoverRing,
        },
      };
    },
    menu: (base) => ({
      ...base,
      ...MENU_STACKING,
    }),
    menuPortal: (base) => ({
      ...base,
      ...MENU_STACKING,
    }),
    placeholder: (state) => ({
      ...state,
      color: hasError ? 'var(--color-red-500)' : 'var(--color-gray-400)',
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '2px 12px',
    }),
    singleValue: (base, state) => ({
      ...base,
      color: state.isDisabled ? 'var(--color-gray-400)' : 'var(--color-gray-700)',
      margin: 0,
    }),
    multiValueLabel: (state) => ({
      ...state,
      color: 'var(--color-gray-700)',
      backgroundColor: 'var(--color-primary-100)',
      borderTop: '1px solid var(--color-primary-700)',
      borderBottom: '1px solid var(--color-primary-700)',
      borderLeft: '1px solid var(--color-primary-700)',
    }),
    multiValueRemove: (state) => ({
      ...state,
      color: 'var(--color-gray-700)',
      backgroundColor: 'var(--color-primary-100)',
      transition: 'all 0.3s ease-in-out',
      borderTop: '1px solid var(--color-primary-700)',
      borderBottom: '1px solid var(--color-primary-700)',
      borderRight: '1px solid var(--color-primary-700)',
      '&:hover': {
        color: 'var(--color-primary-200)',
        backgroundColor: 'var(--color-primary-700)',
      },
    }),
  };
}
